import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import StaffUtils from 'services/StaffUtils';

const HEARTBEAT = 25 * 1000;

// The lock is global per document while the guards around it are per hook
// instance, so ownership is reference counted for the whole tab: two mounted
// instances of the same document must not release each other's lock.
const documentOwners = new Map<string, number>();

function addOwner(documentId: string) {
  documentOwners.set(documentId, (documentOwners.get(documentId) ?? 0) + 1);
}

/** Returns true when the caller was the last owner, i.e. the lock can be released. */
function removeOwner(documentId: string) {
  const remaining = (documentOwners.get(documentId) ?? 1) - 1;
  if (remaining > 0) {
    documentOwners.set(documentId, remaining);
    return false;
  }
  documentOwners.delete(documentId);
  return true;
}

function useLockDocument(documentId: string, shouldLock: boolean) {
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  // documentId of the lock request currently in flight, if any
  const lockRequestInProgress = useRef<string | null>(null);
  // the document this instance actually holds the lock for, if any. The release
  // has to key off this rather than off redux, which the `locked-document`
  // websocket broadcast can flip to another user at any moment.
  const ownedDocumentRef = useRef<string | null>(null);
  // what the caller currently wants, or null once unmounted; a lock request
  // that lands afterwards must not be treated as still wanted
  const targetRef = useRef<{ documentId: string; shouldLock: boolean } | null>(null);

  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);
  const lockedToId = useAppSelector((state) => state.documents.locked[documentId])!;

  // `user` is cleared on logout / session expiry while a document can still be mounted
  const ownId = authentication.user?.id;

  const [loading, setLoading] = useState(!lockedToId && shouldLock);
  const [lockFailed, setLockFailed] = useState(false);

  const lockedStaff = admins[lockedToId] || physicians[lockedToId];

  const requestLock = useCallback(
    (id: string, value: boolean) =>
      dispatch(api.Documents.setLockedDocument({ id, booleanResource: { value } })),
    [dispatch],
  );

  const claimOwnership = useCallback((id: string) => {
    if (ownedDocumentRef.current === id) {
      return;
    }
    ownedDocumentRef.current = id;
    addOwner(id);
  }, []);

  const dropOwnership = useCallback(() => {
    const owned = ownedDocumentRef.current;
    if (!owned) {
      return false;
    }
    ownedDocumentRef.current = null;
    return removeOwner(owned);
  }, []);

  useEffect(() => {
    targetRef.current = { documentId, shouldLock };
  });

  useEffect(
    () => () => {
      targetRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!shouldLock || !ownId || lockedToId || lockRequestInProgress.current === documentId) {
      return;
    }

    lockRequestInProgress.current = documentId;
    setLoading(true);
    setLockFailed(false);

    const finish = (acquired: boolean) => {
      if (lockRequestInProgress.current === documentId) {
        lockRequestInProgress.current = null;
      }

      const target = targetRef.current;
      const stillWanted = !!target && target.documentId === documentId && target.shouldLock;

      if (acquired && stillWanted) {
        claimOwnership(documentId);
      } else if (acquired && !documentOwners.has(documentId)) {
        // the viewer moved on before the lock landed: release it, otherwise the
        // document stays locked server side with nobody heartbeating it
        requestLock(documentId, false);
      }

      if (!target) {
        return;
      }

      setLoading(false);

      if (stillWanted) {
        // a failed request is not retried on its own: without this the caller
        // has no way of knowing the document was never locked
        setLockFailed(!acquired);
      }
    };

    requestLock(documentId, true)
      .then((result) => finish(!!result))
      .catch(() => finish(false));
  }, [claimOwnership, requestLock, documentId, shouldLock, ownId, lockedToId]);

  useEffect(() => {
    if (ownId && lockedToId && lockedToId !== ownId) {
      // another user took the lock over, so this instance must not release it
      dropOwnership();
    }

    if (shouldLock && ownId && ownId === lockedToId) {
      claimOwnership(documentId);

      intervalRef.current = setInterval(() => {
        // Only send a new lock request if we're not already processing one
        if (!lockRequestInProgress.current) {
          lockRequestInProgress.current = documentId;
          requestLock(documentId, true).finally(() => {
            if (lockRequestInProgress.current === documentId) {
              lockRequestInProgress.current = null;
            }
          });
        }
      }, HEARTBEAT);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [claimOwnership, dropOwnership, requestLock, documentId, shouldLock, ownId, lockedToId]);

  useEffect(
    () => () => {
      // Only unlock if we're the ones who locked it, and only once the last
      // instance holding it is gone
      if (ownedDocumentRef.current === documentId && dropOwnership()) {
        requestLock(documentId, false);
      }
    },
    // `shouldLock` is not read in the body: it is here so that turning locking
    // off (e.g. the document gets approved) runs the release
    [dropOwnership, requestLock, documentId, shouldLock],
  );

  const lockedName = useMemo(() => {
    if (lockedToId) {
      return StaffUtils.getName(lockedStaff);
    }
  }, [lockedToId, lockedStaff]);

  return [lockedToId, lockedName, !!loading, lockFailed];
}

export default useLockDocument;
